// ════════════════════════════════════════════════════════════════════════
//  The app shell — which screen, nothing else.
//
//  Screens are state, not routes. Deliberately no router: a URL a child can land
//  on mid-exercise is only a source of trouble in a classroom.
//
//  Everything the child *has* (code, bundle, progress) lives in useLearner;
//  everything that happens during an exercise lives in useTrainer.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import type { Lesson } from "@otto/lehrgang";
import { openLessons } from "./features/lessons/components/LessonList.js";
import { configFrom, type BackendConfig } from "./shared/lib/backend.js";
import { useLearner } from "./shared/hooks/useLearner.js";
import { LessonOverview } from "./features/lessons/components/LessonOverview.js";
import { Trainer } from "./features/practice/components/Trainer.js";
import { FingerLegend } from "./features/lessons/components/FingerLegend.js";
import { StartScreen } from "./features/start/components/StartScreen.js";
import { CourseChoice } from "./features/courses/components/CourseChoice.js";
import { AssessmentScreen } from "./features/assessment/components/AssessmentScreen.js";
import { readCode } from "./features/session/lib/session.js";
import styles from "./shared/components/shell.module.css";

type View =
  | { name: "start" }
  | { name: "lessons" }
  | { name: "lesson"; lesson: Lesson }
  | { name: "test" };

const CONFIG_FROM_ENV = configFrom(import.meta.env as Record<string, string>);

/**
 * `config` and `fetchFn` are injectable so the code sign-in stays testable.
 * Without configuration the app runs in guest mode only — exactly what the pilot
 * wants.
 */
export function App({
  config = CONFIG_FROM_ENV,
  fetchFn = fetch,
}: {
  config?: BackendConfig | null;
  fetchFn?: typeof fetch;
} = {}) {
  const learner = useLearner({ config, fetchFn });
  const [view, setView] = useState<View>(() =>
    readCode() === null ? { name: "start" } : { name: "lessons" },
  );

  if (view.name === "start") {
    return (
      <StartScreen
        bundle={learner.bundle}
        onCode={(entered) => {
          void learner.signInWithCode(entered).then(() => {
            if (readCode() !== null) setView({ name: "lessons" });
          });
        }}
        onGuest={() => setView({ name: "lessons" })}
        error={learner.signInError}
        loading={learner.signingIn}
      />
    );
  }

  // Vor allem anderen: solange die Wahl aussteht, gibt es nichts zu zeigen.
  // Kein eigener View-Zustand dafür - der müsste mit dem Learner synchron
  // gehalten werden, und genau dort entstehen die Bildschirme, die sich nicht
  // mehr schließen lassen.
  if (learner.needsCourseChoice) {
    return (
      <CourseChoice
        courses={learner.courses}
        onChoose={(slug) => {
          learner.chooseCourse(slug);
          setView({ name: "lessons" });
        }}
      />
    );
  }

  // Der Test steht vor den Lektionen, aber nicht vor der Lehrgangswahl: erst
  // muss feststehen, WELCHEN Test das Kind macht.
  if (view.name === "test" && learner.bundle.assessment && learner.assessment) {
    return (
      <div className={styles.page}>
        <AssessmentScreen
          assessment={learner.bundle.assessment}
          phase={learner.assessment.phase}
          before={learner.assessment.before ?? undefined}
          onDone={(measurement) => {
            learner.saveMeasurement(learner.assessment!.phase, measurement);
            setView({ name: "lessons" });
          }}
          onCancel={() => setView({ name: "lessons" })}
        />
      </div>
    );
  }

  if (view.name === "lesson") {
    // Nur die offenen Lektionen: sonst führte "Weiter zu Lektion 4" im
    // Gastmodus an der Sperre vorbei, die die Übersicht gerade aufgebaut hat.
    const ordered = openLessons(learner.bundle, {
      hasCode: learner.code !== null,
      progress: learner.progress,
      gate: learner.gate,
    });
    const position = ordered.findIndex((l) => l.key === view.lesson.key);
    const next = position >= 0 ? (ordered[position + 1] ?? null) : null;

    return (
      <div className={styles.page}>
        <Trainer
          key={view.lesson.key}
          bundle={learner.bundle}
          lesson={view.lesson}
          nextLesson={next}
          onBack={() => setView({ name: "lessons" })}
          onSwitchLesson={(lesson) => setView({ name: "lesson", lesson })}
          onStageDone={learner.remember}
        />
        <FingerLegend bundle={learner.bundle} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <LessonOverview
        bundle={learner.bundle}
        gate={learner.gate}
        assessment={learner.assessment}
        onAssessment={() => setView({ name: "test" })}
        origin={learner.origin}
        progress={learner.progress}
        hasCode={learner.code !== null}
        onChoose={(lesson) => setView({ name: "lesson", lesson })}
        courseTitle={learner.course?.title ?? null}
        onSwitchCourse={
          learner.courses.length > 1
            ? () => {
                learner.switchCourse();
                setView({ name: "lessons" });
              }
            : null
        }
        onForgetProgress={learner.forgetProgress}
        onFinish={() => {
          learner.finish();
          setView({ name: "start" });
        }}
      />
    </div>
  );
}
